import { useEffect, useRef, useState } from 'react';

import useSetTimeout from '../../hooks/useSetTimeout';

const DEFAULT_CHAR_INTERVAL_MS = 20;
const DEFAULT_TEXT = '';

export enum ChunkMode {
  Char = 'char',
  Word = 'word',
}

type Props = {
  className?: string;
  isAnimate: boolean;
  chunkMode?: ChunkMode;
  charIntervalMs?: number;
  children: string;
};

const splitText = (text: string, mode: ChunkMode): string[] => {
  if (!text) {
    return [];
  }
  if (mode === ChunkMode.Char) {
    return text.split('');
  }

  return text.split(/(\s+)/).filter((part) => part.length > 0);
};

const getDelayMs = (chunk: string, mode: ChunkMode, baseInterval: number): number => {
  if (mode === ChunkMode.Word) {
    return Math.max(1, chunk.length) * baseInterval;
  }

  return baseInterval;
};

export const EmulatedStreamingText = ({
  children,
  isAnimate,
  chunkMode = ChunkMode.Char,
  charIntervalMs = DEFAULT_CHAR_INTERVAL_MS,
  className,
}: Props) => {
  const [displayedText, setDisplayedText] = useState(DEFAULT_TEXT);

  const queueRef = useRef<string[]>([]);
  const timerRef = useRef<number | null>(null);

  const setTimeout = useSetTimeout();

  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!isAnimate) {
      queueRef.current = [];
      setDisplayedText(children);
      return;
    }

    const tick = () => {
      timerRef.current = null;

      if (queueRef.current.length === 0) {
        return;
      }

      const chunk = queueRef.current.shift();
      if (!chunk) {
        return;
      }

      setDisplayedText((prev) => prev + chunk);

      const delay = getDelayMs(chunk, chunkMode, charIntervalMs);
      timerRef.current = setTimeout(tick, delay);
    };

    setDisplayedText(DEFAULT_TEXT);
    queueRef.current = splitText(children, chunkMode);
    timerRef.current = setTimeout(tick, charIntervalMs);
  }, [children, isAnimate, chunkMode, charIntervalMs, setTimeout]);

  return <div className={className}>{displayedText}</div>;
};
